import { useEffect, useState, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import PageShell from "@/components/PageShell";
import api, { apiError } from "@/lib/api";
import { formatRupiah, formatNumber, formatDate, todayWITA } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { StatusBadge, RescheduleBadge } from "@/components/StatusBadge";
import { TrancheProgress } from "@/components/TrancheProgress";
import { toast } from "sonner";
import DatePicker from "@/components/DatePicker";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2 } from "lucide-react";
import { ScheduleDates } from "@/components/ScheduleDates";
import { SupplierName } from "@/components/SupplierName";

export default function Payments() {
  return (
    <PageShell title="Jadwal Pembayaran">
      <Tabs defaultValue="create">
        <TabsList>
          <TabsTrigger value="create" data-testid="tab-create-schedule">Buat Jadwal</TabsTrigger>
          <TabsTrigger value="list" data-testid="tab-list-schedule">Daftar Jadwal</TabsTrigger>
        </TabsList>
        <TabsContent value="create"><CreateSchedule /></TabsContent>
        <TabsContent value="list"><ScheduleList /></TabsContent>
      </Tabs>
    </PageShell>
  );
}

function CreateSchedule() {
  const [searchParams] = useSearchParams();
  const [invoices, setInvoices] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [supplierFilter, setSupplierFilter] = useState(searchParams.get("supplier") || "all");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState([]);
  const [credits, setCredits] = useState({ credits: {}, available: 0 });
  const [selectedReturns, setSelectedReturns] = useState([]);
  const [method, setMethod] = useState("transfer");
  const [payDate, setPayDate] = useState(todayWITA());
  const [warkatNo, setWarkatNo] = useState("");
  const [warkatBank, setWarkatBank] = useState("");
  const [split, setSplit] = useState(false);
  const [tranches, setTranches] = useState([{ amount: "", method: "giro", cair_date: todayWITA(), warkat_number: "", warkat_bank: "" }]);

  const load = useCallback(() => {
    const p = new URLSearchParams({ status: "UNSCHEDULED" });
    if (supplierFilter !== "all") p.set("supplier_id", supplierFilter);
    if (q) p.set("q", q);
    api.get(`/invoices?${p.toString()}`).then((r) => setInvoices(r.data));
  }, [supplierFilter, q]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/suppliers").then((r) => setSuppliers(r.data)); }, []);

  const selectedInvoices = invoices.filter((i) => selected.includes(i.id));
  const activeSupplier = selectedInvoices[0]?.supplier_id || null;

  useEffect(() => {
    if (activeSupplier) api.get(`/returns/credits?supplier_id=${activeSupplier}`).then((r) => setCredits(r.data));
    else setCredits({ credits: {}, available: 0 });
    setSelectedReturns([]);
  }, [activeSupplier]);

  const toggle = (inv) => {
    if (selected.includes(inv.id)) {
      setSelected(selected.filter((x) => x !== inv.id));
    } else {
      if (activeSupplier && inv.supplier_id !== activeSupplier) {
        return toast.error("Hanya nota dari satu supplier yang sama dapat dipilih");
      }
      setSelected([...selected, inv.id]);
    }
  };

  const availableReturns = Object.entries(credits.credits).filter(([, v]) => v.available > 0);

  const allocs = useMemo(() => {
    const result = [];
    const cap = {};
    selectedInvoices.forEach((i) => { cap[i.id] = i.total_amount; });
    selectedReturns.forEach((rid) => {
      const v = credits.credits[rid];
      if (!v) return;
      let remaining = v.available;
      for (const inv of selectedInvoices) {
        if (remaining <= 0.001) break;
        const take = Math.min(remaining, cap[inv.id]);
        if (take > 0.001) {
          result.push({ return_id: rid, invoice_id: inv.id, amount: Math.round(take * 100) / 100 });
          cap[inv.id] -= take;
          remaining -= take;
        }
      }
    });
    return result;
  }, [selectedReturns, selectedInvoices, credits]);

  const creditUsed = allocs.reduce((a, x) => a + x.amount, 0);
  const netTotal = selectedInvoices.reduce((a, i) => a + i.total_amount, 0) - creditUsed;

  const submit = async () => {
    if (selected.length === 0) return toast.error("Pilih minimal satu nota");
    try {
      let payload = {
        supplier_id: activeSupplier, invoice_ids: selected,
        allocations: allocs,
      };
      if (split) {
        const trs = tranches.filter((t) => Number(t.amount) > 0);
        if (trs.length === 0) return toast.error("Isi minimal satu termin pembayaran");
        const sum = trs.reduce((a, t) => a + Number(t.amount), 0);
        if (Math.abs(sum - netTotal) > 1) return toast.error(`Total termin (${formatRupiah(sum)}) harus sama dengan pembayaran bersih (${formatRupiah(netTotal)})`);
        payload = { ...payload, method: "split", tranches: trs.map((t) => ({ amount: Number(t.amount), method: t.method, cair_date: t.cair_date, warkat_number: t.method === "giro" ? t.warkat_number : null, warkat_bank: t.method === "giro" ? t.warkat_bank : null })) };
      } else {
        if (method === "giro" && (!warkatNo || !warkatBank)) return toast.error("Lengkapi data warkat giro");
        if (!payDate) return toast.error("Isi tanggal pembayaran");
        payload = { ...payload, method, payment_date: payDate, warkat_number: method === "giro" ? warkatNo : null, warkat_bank: method === "giro" ? warkatBank : null };
      }
      await api.post("/schedules", payload);
      toast.success("Jadwal pembayaran berhasil dibuat");
      setSelected([]); setSelectedReturns([]); setWarkatNo(""); setWarkatBank(""); setSplit(false);
      setTranches([{ amount: "", method: "giro", cair_date: todayWITA(), warkat_number: "", warkat_bank: "" }]);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="grid gap-6 lg:grid-cols-3 mt-4">
      <div className="lg:col-span-2 space-y-3">
        <div className="flex gap-3">
          <Input placeholder="Cari no. nota" value={q} onChange={(e) => setQ(e.target.value)} className="w-48" data-testid="schedule-invoice-search" />
          <Select value={supplierFilter} onValueChange={(v) => { setSupplierFilter(v); setSelected([]); setSelectedReturns([]); }}>
            <SelectTrigger className="w-48" data-testid="schedule-supplier-filter"><SelectValue placeholder="Supplier" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Supplier</SelectItem>
              {suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Card className="shadow-sm"><CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead className="w-10"></TableHead><TableHead>No. Nota</TableHead><TableHead>Supplier</TableHead>
              <TableHead>Tgl</TableHead><TableHead className="text-right">Roll</TableHead><TableHead className="text-right">Nilai</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {invoices.map((i) => {
                const sName = suppliers.find((s) => s.id === i.supplier_id)?.name;
                const disabled = activeSupplier && i.supplier_id !== activeSupplier;
                return (
                  <TableRow key={i.id} className={disabled ? "opacity-40" : "hover:bg-slate-50"} data-testid={`sched-invoice-row-${i.code}`}>
                    <TableCell><Checkbox checked={selected.includes(i.id)} onCheckedChange={() => toggle(i)} disabled={disabled} data-testid={`sched-check-${i.code}`} /></TableCell>
                    <TableCell className="font-medium">{i.invoice_number}</TableCell>
                    <TableCell className="text-xs"><SupplierName name={sName} /></TableCell>
                    <TableCell>{formatDate(i.invoice_date)}</TableCell>
                    <TableCell className="text-right font-mono">{formatNumber(i.total_rolls)}</TableCell>
                    <TableCell className="text-right font-mono font-semibold">{formatRupiah(i.total_amount)}</TableCell>
                  </TableRow>
                );
              })}
              {invoices.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-8">Tidak ada nota belum terjadwal.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent></Card>
      </div>

      <div className="space-y-4">
        <Card className="shadow-sm sticky top-20">
          <CardHeader className="pb-3"><CardTitle className="text-base">Ringkasan Jadwal</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="text-sm">
              <div className="flex justify-between"><span className="text-slate-500">Nota dipilih</span><span className="font-semibold">{selected.length}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Total roll</span><span className="font-semibold font-mono">{formatNumber(selectedInvoices.reduce((a, i) => a + i.total_rolls, 0))}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Kredit tersedia</span><span className="font-semibold font-mono text-emerald-600">{formatRupiah(credits.available)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Potongan retur</span><span className="font-semibold font-mono text-purple-600" data-testid="schedule-credit-used">− {formatRupiah(creditUsed)}</span></div>
            </div>

            {selected.length > 0 && availableReturns.length > 0 && (
              <div className="space-y-2">
                <Label className="text-xs">Nota Retur (potong otomatis)</Label>
                <div className="space-y-1.5 max-h-40 overflow-y-auto">
                  {availableReturns.map(([rid, v]) => (
                    <label key={rid} className="flex items-center gap-2 bg-slate-50 p-2 rounded cursor-pointer" data-testid={`return-select-${v.code}`}>
                      <Checkbox checked={selectedReturns.includes(rid)} onCheckedChange={() => setSelectedReturns(selectedReturns.includes(rid) ? selectedReturns.filter((x) => x !== rid) : [...selectedReturns, rid])} data-testid={`return-check-${v.code}`} />
                      <div className="flex-1 flex justify-between text-xs">
                        <span className="font-mono">{v.code}</span>
                        <span className="font-semibold text-emerald-600">− {formatRupiah(v.available)}</span>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div className="flex items-center gap-2 pt-1">
              <Switch checked={split} onCheckedChange={setSplit} data-testid="split-toggle" />
              <Label className="text-xs">Pembayaran Totalan (pecah ke beberapa BG/termin)</Label>
            </div>

            {!split ? (
              <>
                <div className="space-y-2">
                  <Label className="text-xs">Metode Pembayaran</Label>
                  <RadioGroup value={method} onValueChange={setMethod} className="flex gap-4">
                    <div className="flex items-center gap-1.5"><RadioGroupItem value="transfer" id="m-transfer" data-testid="method-transfer" /><Label htmlFor="m-transfer" className="text-[0.8125rem] font-normal">Transfer</Label></div>
                    <div className="flex items-center gap-1.5"><RadioGroupItem value="giro" id="m-giro" data-testid="method-giro" /><Label htmlFor="m-giro" className="text-[0.8125rem] font-normal">Bilyet Giro</Label></div>
                  </RadioGroup>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">{method === "giro" ? "Tanggal Pencairan" : "Tanggal Transfer"}</Label>
                  <DatePicker value={payDate} onChange={setPayDate} testid="schedule-paydate-input" />
                </div>
                {method === "giro" && (
                  <div className="space-y-2">
                    <Input placeholder="Nomor Warkat" value={warkatNo} onChange={(e) => setWarkatNo(e.target.value)} data-testid="warkat-number-input" />
                    <Input placeholder="Nama Bank Warkat" value={warkatBank} onChange={(e) => setWarkatBank(e.target.value)} data-testid="warkat-bank-input" />
                  </div>
                )}
              </>
            ) : (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-xs">Termin Pembayaran (BG)</Label>
                  <Button type="button" variant="outline" size="sm" onClick={() => setTranches([...tranches, { amount: "", method: "giro", cair_date: todayWITA(), warkat_number: "", warkat_bank: "" }])} data-testid="add-tranche-btn"><Plus className="h-3 w-3" /></Button>
                </div>
                {tranches.map((t, idx) => (
                  <div key={idx} className="bg-slate-50 p-2 rounded space-y-1" data-testid={`tranche-row-${idx}`}>
                    <div className="flex gap-1">
                      <Input type="number" placeholder="Nominal" className="h-8 text-xs" value={t.amount} onChange={(e) => setTranches(tranches.map((x, i) => i === idx ? { ...x, amount: e.target.value } : x))} data-testid={`tranche-amount-${idx}`} />
                      <Select value={t.method} onValueChange={(v) => setTranches(tranches.map((x, i) => i === idx ? { ...x, method: v } : x))}>
                        <SelectTrigger className="h-8 text-xs w-24" data-testid={`tranche-method-${idx}`}><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="giro">Giro</SelectItem><SelectItem value="transfer">Transfer</SelectItem></SelectContent>
                      </Select>
                      {tranches.length > 1 && <Button type="button" variant="ghost" size="icon" className="h-8 w-8" onClick={() => setTranches(tranches.filter((_, i) => i !== idx))}><Trash2 className="h-3.5 w-3.5 text-rose-500" /></Button>}
                    </div>
                    <DatePicker value={t.cair_date} onChange={(v) => setTranches(tranches.map((x, i) => i === idx ? { ...x, cair_date: v } : x))} testid={`tranche-date-${idx}`} />
                    {t.method === "giro" && (
                      <div className="flex gap-1">
                        <Input placeholder="No. Warkat" className="h-8 text-xs" value={t.warkat_number} onChange={(e) => setTranches(tranches.map((x, i) => i === idx ? { ...x, warkat_number: e.target.value } : x))} data-testid={`tranche-warkat-${idx}`} />
                        <Input placeholder="Bank" className="h-8 text-xs" value={t.warkat_bank} onChange={(e) => setTranches(tranches.map((x, i) => i === idx ? { ...x, warkat_bank: e.target.value } : x))} data-testid={`tranche-bank-${idx}`} />
                      </div>
                    )}
                  </div>
                ))}
                <div className="flex justify-between text-xs px-1">
                  <span className="text-slate-500">Total termin</span>
                  <span className={`font-mono font-semibold ${Math.abs(tranches.reduce((a, t) => a + (Number(t.amount) || 0), 0) - netTotal) > 1 ? "text-rose-600" : "text-emerald-600"}`} data-testid="tranche-sum">{formatRupiah(tranches.reduce((a, t) => a + (Number(t.amount) || 0), 0))}</span>
                </div>
              </div>
            )}

            <div className="total-box rounded-2xl p-4 text-right">
              <div className="text-xs text-slate-300">Pembayaran Bersih</div>
              <div className="font-mono font-bold text-lg" data-testid="schedule-net-amount">{formatRupiah(netTotal)}</div>
            </div>
            <Button className="w-full" onClick={submit} data-testid="create-schedule-btn">Simpan Jadwal</Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function ScheduleList() {
  const [schedules, setSchedules] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [filter, setFilter] = useState({ status: "all", method: "all" });

  const load = useCallback(() => {
    const p = new URLSearchParams();
    if (filter.status !== "all") p.set("status", filter.status);
    if (filter.method !== "all") p.set("method", filter.method);
    api.get(`/schedules?${p.toString()}`).then((r) => setSchedules(r.data));
  }, [filter]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/suppliers").then((r) => setSuppliers(r.data)); }, []);
  const supName = (id) => suppliers.find((s) => s.id === id)?.name || "-";

  return (
    <div className="space-y-3 mt-4">
      <div className="flex gap-3">
        <Select value={filter.status} onValueChange={(v) => setFilter({ ...filter, status: v })}>
          <SelectTrigger className="w-40" data-testid="schedule-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent><SelectItem value="all">Semua</SelectItem><SelectItem value="active">Aktif</SelectItem><SelectItem value="settled">Lunas</SelectItem><SelectItem value="superseded">Digantikan</SelectItem></SelectContent>
        </Select>
        <Select value={filter.method} onValueChange={(v) => setFilter({ ...filter, method: v })}>
          <SelectTrigger className="w-40" data-testid="schedule-method-filter"><SelectValue placeholder="Metode" /></SelectTrigger>
          <SelectContent><SelectItem value="all">Semua</SelectItem><SelectItem value="transfer">Transfer</SelectItem><SelectItem value="giro">Giro</SelectItem></SelectContent>
        </Select>
      </div>
      <Card className="shadow-sm"><CardContent className="p-0 overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Kode</TableHead><TableHead>Supplier</TableHead><TableHead>Metode</TableHead><TableHead>Tgl Bayar</TableHead>
            <TableHead className="text-right">Pembayaran Bersih</TableHead><TableHead className="text-right">Kredit</TableHead><TableHead>Status</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {schedules.map((s) => (
              <TableRow key={s.id} className="hover:bg-slate-50" data-testid={`schedule-row-${s.code}`}>
                <TableCell className="font-mono text-xs">{s.code}{s.reschedule_badge && <RescheduleBadge />}</TableCell>
                <TableCell><SupplierName name={supName(s.supplier_id)} id={s.supplier_id} /></TableCell>
                <TableCell className="capitalize">{s.method === "split" ? `Totalan (${s.tranches?.length || 0} BG)` : s.method}{s.method === "giro" && <div className="text-[11px] text-slate-500">{s.warkat_bank} · {s.warkat_number}</div>}{s.method === "split" && <div className="mt-1.5 normal-case"><TrancheProgress tranches={s.tranches} testid={`pay-progress-${s.code}`} /></div>}</TableCell>
                <TableCell><ScheduleDates s={s} testid={`pay-dates-${s.code}`} /></TableCell>
                <TableCell className="text-right font-mono font-semibold">{formatRupiah(s.net_amount)}</TableCell>
                <TableCell className="text-right font-mono text-purple-600">{formatRupiah(s.credit_used)}</TableCell>
                <TableCell><StatusBadge status={s.status} /></TableCell>
              </TableRow>
            ))}
            {schedules.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-8">Belum ada jadwal.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </CardContent></Card>
    </div>
  );
}
